import { removeEmptyPropertiesFromNestedObjects } from '@mae/utils/objectUtils';
import { isStringAndNotNullOrEmpty } from '@mae/utils/types';
import type { Direction, ThemeOptions } from '@mui/material';
import { amber } from '@mui/material/colors';
import { deepmerge as muideepmerge } from '@mui/utils';
import { DeepmergeOptions } from '@mui/utils/deepmerge';
import { makeAutoObservable } from 'mobx';
import defaultMAEColorScheme from './defaultMAEColorScheme.json';
import { getMaeLightDarkMode, getSystemSetting, LightDarkType, MuiLightDarkType } from './utils';

export type DefaultSettingsType = {
	primaryColor?: string;
	secondaryColor?: string;
	direction?: Direction;
};

function getColorFromVariable(variable: string): string {
	return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
}

function deepmerge(target, source, options?: DeepmergeOptions) {
	const prunedSource = removeEmptyPropertiesFromNestedObjects(source);
	return muideepmerge(target, prunedSource, options);
}

class ThemeSettingsStore {
	private _defaults: DefaultSettingsType;
	private get defaults(): DefaultSettingsType {
		return this._defaults;
	}

	private _lightDarkMode: LightDarkType = 'light';
	public get lightDarkMode(): LightDarkType {
		return this._lightDarkMode;
	}

	private _themeOptions: ThemeOptions = {};
	public get themeOptions(): ThemeOptions {
		return this._themeOptions;
	}

	constructor() {
		makeAutoObservable(this);
	}

	private getMuiLightDarkType(lightDarkMode: LightDarkType): MuiLightDarkType {
		return lightDarkMode === 'system' ? getSystemSetting() : lightDarkMode;
	}

	private createDefaultThemeOptions() {
		const maeLightDarkMode = getMaeLightDarkMode();
		const mode = this.getMuiLightDarkType(maeLightDarkMode);

		// non-studio apps will need to populate these color values manually.
		// perhaps we automatically read this from the token?
		this._themeOptions = {
			palette: {
				mode: mode,
				primary: {
					main: this.defaults?.primaryColor || getColorFromVariable('--hxg-primary') || defaultMAEColorScheme.primaryColor
				},
				secondary: {
					main: this.defaults?.secondaryColor || getColorFromVariable('--hxg-secondary') || defaultMAEColorScheme.secondaryColor
				},
				warning: {
					main: mode === 'dark' ? amber[300] : amber[500]
				}
			},
			direction: this.defaults?.direction === 'rtl' || document.body.classList.contains('rtl') ? 'rtl' : 'ltr'
		};

		this.setLightDarkMode(getMaeLightDarkMode());
	}

	public initialize(defaults?: DefaultSettingsType): void {
		this._defaults = defaults;
		this.createDefaultThemeOptions();
		// this enables cross instance sharing of the light/dark mode setting.
		// this mostly works but needs additional testing.
		window.addEventListener('storage', (ev: StorageEvent) => {
			if (ev.key === 'dark-theme' && ev.oldValue !== ev.newValue) {
				switch (ev.newValue) {
					case 'true':
						this.setLightDarkMode('dark');
						break;
					case 'false':
						this.setLightDarkMode('light');
						break;
					case null:
						this.setLightDarkMode('system');
						break;
					default:
						console.log('light-dark-mode: invalid option reveiced from storage event listener');
						break;
				}
			}
		});
	}

	// Actions
	public reset() {
		this.createDefaultThemeOptions();
	}

	public setThemeOptions(value: ThemeOptions) {
		this._themeOptions = deepmerge(this._themeOptions, value);
	}

	public setLightDarkMode(value: LightDarkType): void {
		if (value !== 'system') {
			window.localStorage.setItem('dark-theme', value === 'dark' ? 'true' : 'false');
		} else {
			window.localStorage.removeItem('dark-theme');
		}
		this._lightDarkMode = value;
		this._themeOptions = deepmerge(this._themeOptions, { palette: { mode: this.getMuiLightDarkType(value) } } as ThemeOptions);
	}

	public setTextDirection(value: Direction): void {
		if (value !== 'ltr' && value !== 'rtl') {
			this._themeOptions = deepmerge(this.themeOptions, { direction: 'ltr' } as ThemeOptions);
		} else {
			this._themeOptions = deepmerge(this.themeOptions, { direction: value } as ThemeOptions);
		}
	}

	public setPrimaryColor(value: string): void {
		if (!isStringAndNotNullOrEmpty(value)) {
			this._themeOptions = deepmerge(this._themeOptions, {
				palette: { primary: { main: defaultMAEColorScheme.primaryColor } }
			} as ThemeOptions);
		} else {
			this._themeOptions = deepmerge(this._themeOptions, { palette: { primary: { main: value } } } as ThemeOptions);
		}
	}

	public setSecondaryColor(value: string): void {
		if (!isStringAndNotNullOrEmpty(value)) {
			this._themeOptions = deepmerge(this._themeOptions, {
				palette: { secondary: { main: defaultMAEColorScheme.secondaryColor } }
			} as ThemeOptions);
		} else {
			this._themeOptions = deepmerge(this._themeOptions, { palette: { secondary: { main: value } } } as ThemeOptions);
		}
	}

	public getPrimaryColor(): string {
		return this.themeOptions.palette.primary['main'];
	}

	public getSecondaryColor(): string {
		return this.themeOptions.palette.secondary['main'];
	}
}

export type { ThemeSettingsStore };
const themeSettingsStore = new ThemeSettingsStore();

export { themeSettingsStore };

window['themeSettingsStore'] = themeSettingsStore;
